// The workflow catalogue: every agent this frontend knows about, and the
// long-form detail each one renders in its detail panel. Data only — the
// surfaces that present it (home card grids, sidebar, command palette,
// detail panel) each own their own rendering and filter this list against
// the workspace's visibility registry.
import type React from 'react';
import { FileText, Presentation, FileSpreadsheet, BarChart3, Globe, Network, Newspaper, LayoutDashboard, ClipboardList, ClipboardCheck, Calculator, Zap, LayoutGrid, Target, DollarSign, MapPin, MonitorPlay, PieChart, Store, Package } from 'lucide-react';

// ── Data Definitions ────────────────────────────────────────────────────

export interface WorkflowDetailContent {
  description: string;
  howToUse: string;
  /**
   * `howToUse` for a workspace whose input card is driven by a curated SKU
   * list rather than the brand/subbrand tree. Those cards expose a different
   * set of controls, so the default instructions name fields the reader
   * cannot see. Only workflows with a curated mode define this; the detail
   * panel falls back to `howToUse` when it is absent.
   *
   * The panel's gate is `useCuratedSkus`, which reads the Category Market
   * Share config key. A second workflow adopting this field must widen that
   * hook first, or its copy switches on another workflow's config.
   */
  howToUseCurated?: string;
  useCases: string[];
  storagePath?: string;
  exampleImages?: string[];
  outputSummary: string;
  keyFeatures: string[];
}

export interface WorkflowAgent {
  id: string;
  name: string;
  description: string;
  outputType: string;
  outputIcon: React.ElementType;
  hasInputDialog?: boolean;
  isUpcoming?: boolean;
  isNavigationShortcut?: boolean;
  isDashboard?: boolean;
  path?: string;
  detail?: WorkflowDetailContent;
}

export const workflowAgents: WorkflowAgent[] = [
  {
    id: "automated-insight-nba",
    name: "Automated Insight: Next Best Action",
    description: "Insight Alerts, Root Cause, Recommend Action.",
    outputType: "Insights",
    outputIcon: Zap,
    isNavigationShortcut: true,
  },
  {
    id: "single-franchise-audit",
    name: "Single Franchise Audit",
    description: "Deep dive on a specific franchise's performance and compliance across platforms.",
    outputType: "Report",
    outputIcon: FileText,
    hasInputDialog: true,
  },
  {
    id: "agent-live-audit",
    name: "Agent Live Audit",
    description: "Watch an agent order from a chain's live site and see where the drink decision sits.",
    outputType: "Report",
    outputIcon: MonitorPlay,
    hasInputDialog: true,
  },
  {
    id: "multi-franchise-audit",
    name: "Multi-Franchise Audit",
    description: "Audit multiple franchises listing your brand. Output is a sortable, exportable table.",
    outputType: "Excel",
    outputIcon: FileSpreadsheet,
    hasInputDialog: true,
  },
  {
    id: "fsa-deepdive-v2",
    name: "FSA Platform Deepdive",
    description: "Platform meeting prep deck: brand presence funnel, performance trends, cross-platform benchmarks, and geo/cuisine gaps.",
    outputType: "Deck",
    outputIcon: Presentation,
    hasInputDialog: true,
  },
  {
    id: "brand-performance-deepdive",
    name: "Brand Performance Deepdive",
    description: "Analyze brand/subbrand performance with optional benchmark comparisons across markets and platforms.",
    outputType: "Deck",
    outputIcon: Presentation,
    hasInputDialog: true,
  },
  {
    id: "roi-calculator",
    name: "FSA Profit Calculator",
    description: "Calculate GMV impact from improving availability and menu execution with interactive calculator and deck.",
    outputType: "Calculator",
    outputIcon: Calculator,
    hasInputDialog: true,
  },
  {
    id: "performance-news-report",
    name: "FSA News Update",
    description: "AI-powered news search on food delivery platforms with curated insights for ecommerce managers.",
    outputType: "Report",
    outputIcon: Newspaper,
    hasInputDialog: true,
  },
  {
    id: "country-insight-deck",
    name: "Country QBR Deck",
    description: "QBR-style deck with market universe, availability, combos, competitor analysis and key takeaways.",
    outputType: "Deck",
    outputIcon: LayoutDashboard,
    hasInputDialog: true,
  },
  {
    id: "custom-data-export",
    name: "Data Export",
    description: "Export raw outlet or portfolio data with flexible filters, column selection, and pivot-ready formatting for offline analysis.",
    outputType: "Excel",
    outputIcon: FileSpreadsheet,
    hasInputDialog: true,
  },
  {
    id: "cuisine-audit-deck",
    name: "Cuisine Type Audit",
    description: "Deep dive on cuisine types with availability, combos, image metrics, and competitor analysis.",
    outputType: "Deck",
    outputIcon: LayoutDashboard,
    hasInputDialog: true,
  },
  {
    id: "sku-name-audit",
    name: "SKU Name Audit",
    description: "Analyze product naming accuracy across stores and get per-outlet suggestions to align SKU names with brand standards.",
    outputType: "Deck & Excel",
    outputIcon: ClipboardList,
    hasInputDialog: true,
  },
  {
    id: "market-overview",
    name: "Market Overview",
    description: "View DashONE metrics across regions, markets, and platforms with trend analysis.",
    outputType: "Dashboard",
    outputIcon: Globe,
    hasInputDialog: true,
  },
  {
    id: "drag-and-drop",
    name: "Drag and Drop",
    description: "Build live tables and charts by picking any metric and dimension — results update instantly as you change selections or filters.",
    outputType: "Exploration",
    outputIcon: LayoutGrid,
    hasInputDialog: true,
  },
  {
    id: "white-spot-opportunities",
    name: "White Space Opportunities",
    description: "Strategic 2x2 matrix showing high-volume independents not carrying your brand, segmented by region or cuisine.",
    outputType: "Chart",
    outputIcon: Target,
    hasInputDialog: true,
  },
  {
    id: "image-compliance",
    name: "Image Audit",
    description: "Market-level image compliance audit across stores, brands, and SKUs with visual breakdowns.",
    outputType: "Deck + Table",
    outputIcon: FileText,
    hasInputDialog: true,
  },
  {
    id: "menu-price-intelligence",
    name: "Menu Price Intelligence",
    description: "Analyze standalone drink pricing across your market with slide deck insights and interactive explorer.",
    outputType: "Deck + Explorer",
    outputIcon: DollarSign,
    hasInputDialog: true,
  },
  {
    id: "kpi-tracker",
    name: "KPI Tracker",
    description: "Track market share, distribution, and perfect execution KPIs across current, prior period, and last year in a styled Excel report.",
    outputType: "Excel",
    outputIcon: FileSpreadsheet,
    hasInputDialog: true,
  },
  {
    id: "monthly-kpi-report",
    name: "Monthly KPI Report",
    description: "One-pager PDF report rolling up DashONE score, total DSF, platform mix, and AI-classified wins, lost, opportunities, and threats per market. Schedulable monthly via the canvas email scheduler.",
    outputType: "Report",
    outputIcon: FileText,
    hasInputDialog: true,
  },
  {
    id: "monthly-scorecard",
    name: "Monthly Scorecard",
    description: "PEP FR monthly multi-KPI Excel scorecard rolling up 13 sections — FSA presence, market share, portfolio compliance, naming, image audit, combos, pricing, chain compliance.",
    outputType: "Excel",
    outputIcon: FileSpreadsheet,
    hasInputDialog: true,
  },
  {
    id: "platform-distribution-gaps",
    name: "Cross-Platform Brand Gaps",
    description: "Find outlets that list your brands on one platform but miss them on another. Spot the exact outlets a rep should push to add the missing brands to the other platform's menu.",
    outputType: "Deck",
    outputIcon: Network,
    hasInputDialog: true,
  },
  {
    id: "win-back-lost-customers",
    name: "Win-Back Lost Customers",
    description: "Identify outlets that previously listed your brand but no longer do. Prioritize re-acquisition calls by city, platform, and competitor displacement.",
    outputType: "Excel",
    outputIcon: FileSpreadsheet,
    hasInputDialog: true,
  },
  {
    id: "pack-type-gaps",
    name: "Can vs Bottle Pack Gaps",
    description: "Classify every outlet's core-brand listings as can, bottle, or mixed, and spot the pack-type gaps across your portfolio (e.g. cans vs bottles across your core brands).",
    outputType: "Deck + Table",
    outputIcon: Package,
    hasInputDialog: true,
  },
  {
    id: "forge-explorer",
    name: "Opportunity Graph Explorer",
    description: "Interactive DSF and beverage analytics dashboard with cross-filtering, drill-down, and segment analysis across platforms and regions.",
    outputType: "Dashboard",
    outputIcon: BarChart3,
    hasInputDialog: true,
  },
  {
    id: "ons-explorer",
    name: "ONS Dashboard",
    description: "One Number Score dashboard tracking digital storefront availability, core SKU availability, and activation metrics across ASP countries.",
    outputType: "Dashboard",
    outputIcon: BarChart3,
    hasInputDialog: true,
  },
  {
    id: "map-explorer",
    name: "Map Explorer",
    description: "Visually explore your outlet landscape on an interactive map to spot geographic patterns and opportunities.",
    outputType: "Dashboard",
    outputIcon: MapPin,
    hasInputDialog: true,
  },
  {
    id: "crm-audit",
    name: "CRM Audit",
    description: "Audit store performance by manager and sales rep with enriched data and actionable recommendations.",
    outputType: "Report",
    outputIcon: ClipboardList,
    hasInputDialog: true,
  },
  {
    id: "koasp-grab-5star-audit",
    name: "KO ASP Grab 5-Star Audit",
    description: "Audit Coca-Cola's Grab 5-Star outlet universe across 6 SEA markets: KO brand presence, image and name coverage, DashONE score distribution, segment splits, top cities, and trend across refreshes.",
    outputType: "Deck",
    outputIcon: ClipboardCheck,
    hasInputDialog: true,
  },
  {
    id: "1p-qsr-app-audit",
    name: "1P QSR App Audit",
    description: "Audit franchise QSR app compliance for beverage availability, images, combos, and pricing.",
    outputType: "Deck",
    outputIcon: ClipboardCheck,
    hasInputDialog: true,
  },
  {
    id: "1p-epl-app-audit",
    name: "1P EPL App Audit",
    description: "Audit El Pollo Loco 1P app for Coca-Cola beverage compliance, pricing, and specialty drinks.",
    outputType: "Deck",
    outputIcon: ClipboardCheck,
    hasInputDialog: true,
  },
  {
    id: "ou-tracking",
    name: "OU Tracking",
    description: "Executive dashboard tracking performance across Operating Units and countries. Compare KPIs, benchmark markets, and identify improvement opportunities.",
    outputType: "Dashboard",
    outputIcon: LayoutGrid,
    hasInputDialog: true,
  },
  {
    id: "pepsi-ou-tracking",
    name: "DashONE Sector Tracker",
    description: "Executive dashboard tracking PepsiCo performance across Sectors. Compare KPIs, benchmark markets, and identify improvement opportunities.",
    outputType: "Dashboard",
    outputIcon: LayoutGrid,
    hasInputDialog: true,
  },
  {
    id: "category-market-share",
    name: "Category Market Share",
    description: "Analyze brand market share within a product category over time, with trend charts and SKU-level DSF listings.",
    outputType: "Deck + Table",
    outputIcon: PieChart,
    hasInputDialog: true,
  },
  // scaffold:wf-agent — tools/scaffolds/scaffold.sh adds a new workflow's catalogue entry below this line.
];

// ── Detail Content ──────────────────────────────────────────────────────

export const WORKFLOW_DETAIL_CONTENT: Record<string, WorkflowDetailContent> = {
  'agent-live-audit': {
    description: `Send an agent through a restaurant's own ordering flow the way a customer goes through it: the landing page, the store picker, the menu, an item and its options, the cart, and the checkout. Every screen is captured, and the finished audit answers one question about the drinks decision with a screenshot behind each line. You can watch the browser live and click in it yourself while the walk runs. The agent never places an order.`,
    howToUse: `Pick a country and a chain, or paste a link to a store page. Choose where to walk: the chain's own website, Uber Eats, DoorDash, or the link you pasted. Add an address or city if the walk should start somewhere other than the capital, and say in one line what to look at. The live browser opens in this panel; the audit, the recording and every screen appear in the same place when the walk ends, and the result is saved with your other work.`,
    useCases: [
      'Show a customer meeting exactly where the drink sits in a chain\'s own ordering flow',
      'Find out whether the cart and the checkout offer a drink at all',
      'Check whether a brand can be ordered on its own or only inside a combo',
      'Re-run the same walk after a chain changes its site and compare the two',
      'Cover the first-party channel that platform data cannot reach',
    ],
    outputSummary: 'A live browser you can steer, a recording, every screen, and a scored audit',
    keyFeatures: [
      'The agent\'s browser live in the panel, with your own clicks going through to the page',
      'A capture of every screen, with the caption of what the agent was doing',
      'Journey rail showing which stages the walk reached and where it stopped',
      'The drinks decision: orderable alone, price, and whether a combo carries it',
      'Sections scored against a published standard, each line citing its source',
      'A not-measured list with the reason, so an unreached stage never reads as a fail',
      'The recording of the walk, or a replay built from the captured screens',
    ],
  },
  'single-franchise-audit': {
    storagePath: 'single-franchise-audit.mp4',
    description: `Perform a comprehensive audit of any franchise's digital shelf performance across all delivery platforms in a market. The agent analyzes every Digital Store Front (DSF) belonging to the selected franchise — evaluating brand availability, combo meal presence, image quality, and name accuracy — then automatically benchmarks against similar competitors.`,
    howToUse: `Select a country and type the franchise name (e.g. "McDonald's", "KFC", "Domino's"). Optionally filter by platform or market segment. The agent searches all matching DSFs across delivery platforms and generates a full audit deck in about 30–60 seconds. Once generated, you can browse slides, switch to the interactive DSF table for row-level drill-down, or export as PPTX/PDF.`,
    useCases: [
      'Prepare for a Key Account Meeting by reviewing a franchise\'s full digital shelf health before the call',
      'Identify underperforming stores to prioritize for field activation or menu optimization',
      'Compare a franchise\'s performance against automatically selected peer benchmarks',
      'Track month-over-month trends in DashONE Score, availability, and combo penetration',
      'Export the DSF table as CSV to share with field teams or include in internal reports',
    ],
    exampleImages: ['single franchise audit 1.png', 'single franchise audit 2.png'],
    outputSummary: 'Slide deck + interactive DSF table + SKU listings',
    keyFeatures: [
      'Executive summary with DashONE Score, total DSFs, and AI-generated key insights',
      'Platform-by-platform performance breakdown (DSF count, brand share, combo %, images, name accuracy)',
      'Automatic peer benchmark comparison — peers selected by segment, size, and cuisine type',
      'Over-time performance trends (DashONE Score, availability %, combo %) across refreshes',
      'Market share tracking: franchise DSFs ÷ total DSFs with month-over-month delta',
      'Geographic distribution map with DSF density per region/state',
      'Brand & subbrand portfolio coverage — % of stores with full portfolio vs gaps',
      'Stores to prioritize — top 10 underperforming stores with specific action items',
      'SKU audit summary: unique SKUs, coverage distribution, gap opportunities',
      'AI-generated insights & recommendations split into performance, benchmark, and actions',
      'Interactive "All DSF" table with server-side search, sort, filter, and CSV export',
      'SKU Listings tab with per-store product-level data and lazy-loaded filters',
    ],
  },
  'brand-performance-deepdive': {
    description: `Analyze how a specific brand or subbrand performs across markets and platforms, with optional benchmark comparisons. The agent queries brand availability, combo penetration, image compliance, and DashONE scores — then generates a slide deck with trend analysis and a lazy-loaded DSF table for drill-down.`,
    howToUse: `Select a country, choose your primary brand (or subbrand), and optionally pick a benchmark brand for comparison. The agent generates a slide deck in about 20 seconds. Click the DSF tabs to explore individual outlet data with server-side pagination and CSV export.`,
    useCases: [
      'Assess how your brand is performing on a specific platform compared to a competitor brand',
      'Track availability and combo trends over the last 12 months for a quarterly review',
      'Drill down into individual stores to find outlets missing your products',
      'Export outlet-level data for field teams to action on distribution gaps',
    ],
    exampleImages: ['/brand 1.webp', '/brand 2.webp', '/brand 3.webp'],
    storagePath: 'brand-performance-deepdive.mp4',
    outputSummary: 'Slide deck + DSF table with export',
    keyFeatures: [
      'Brand/subbrand selection with optional benchmark brand comparison',
      'Platform-level breakdown of availability, combo, and image metrics',
      'Trend charts showing performance over the last 12 refreshes',
      'Lazy-loaded DSF table with server-side pagination (20 rows/page)',
      'Streaming CSV export for large datasets',
      'Exportable as PPTX or PDF',
    ],
  },
  'white-spot-opportunities': {
    description: `Identify high-potential independent outlets that are not yet carrying your brand. The agent builds a strategic 2×2 matrix plotting Turnover vs Competitive Pressure, highlighting outlets where your brand has the best opportunity to gain distribution.`,
    howToUse: `Select a country and optionally filter by region, cuisine type, or price tier. The agent analyzes independent outlets and plots them on an interactive scatter chart. Click any point to see outlet details. Export the full list for field team activation.`,
    useCases: [
      'Find high-turnover restaurants where competitors are listed but you are not',
      'Prioritize new distribution targets by focusing on low-competition, high-volume outlets',
      'Build a targeted activation list for field teams segmented by region or cuisine',
    ],
    exampleImages: ['/Whitespot 1.webp', '/Whitespot 2.webp', '/Whitespot 3.webp'],
    storagePath: 'white-spot.mp4',
    outputSummary: 'Interactive scatter chart + data table',
    keyFeatures: [
      'Strategic 2×2 matrix: Turnover vs Competitive Pressure',
      'Focus on independents — excludes chains already in your network',
      'Segment by region, cuisine, or price tier',
      'Click-through to individual outlet details',
      'Exportable outlet list for field team activation',
    ],
  },
  'fsa-deepdive-v2': {
    storagePath: 'fsa-deepdive-v2.mp4',
    description: `Prepare for a platform review meeting with a data-backed deck covering where your brand stands on a specific delivery platform, how it has evolved over time, how it compares to other platforms, and where the geo and cuisine gaps are largest.`,
    howToUse: `Select a country and delivery platform (e.g. Lieferando, Uber Eats), choose a store segment, and toggle which sections to include. The result is a 9–11 slide deck with a brand presence funnel, KPI trends, cross-platform benchmarks, and regional/cuisine gap analysis.`,
    useCases: [
      'See exactly where your brand is listed vs. missing on a platform',
      'Track how availability, combo rate, and image rate have changed over time',
      'Benchmark your platform against others in the same market',
      'Identify which cities and cuisines show the largest activation gaps',
      'Generate AI-backed talking points and action items for your platform meeting',
    ],
    keyFeatures: [
      'Brand presence funnel (total DSF → listed → image → combo → fully optimized)',
      'Multi-period trend lines for availability, combo, and image metrics',
      'Cross-platform comparison with market average benchmarks',
      'Regional (state) and city-level gap analysis vs. other platforms',
      'Cuisine segment breakdown showing where brand is underrepresented',
      'AI-generated summary with key findings and concrete action items',
      'Exportable as PPTX',
    ],
    outputSummary: 'Slide deck',
    exampleImages: ['FSA deepdive 1.png', 'FSA deepdive 2.png'],
  },
  'crm-audit': {
    storagePath: 'crm-audit.mp4',
    description: 'Assess store-level performance across your sales team. This workflow combines static CRM assignments with live digital shelf data to identify gaps and generate actionable recommendations for each store visit.',
    howToUse: 'Select a Country, Manager, and optionally a Sales Rep. The report generates a sortable list of all assigned stores enriched with DashONE scores, availability, and combo metrics. Click any store to see a detailed breakdown and specific actions.',
    useCases: [
      'Sales managers reviewing team performance',
      'Sales reps planning their weekly store visits',
      'Identifying underperforming stores with high potential',
      'Checking core SKU availability gaps across a territory'
    ],
    keyFeatures: [
      'Cascading filters: Country → Manager → Sales Rep',
      'Live enrichment: Static store list + dynamic DashONE data',
      'Store Detail Panel: Brand portfolio breakdown and missing SKUs',
      'Smart Recommendations: Rule-based actions for availability and combos',
      'Export to CSV for offline route planning'
    ],
    outputSummary: 'Interactive Report + CSV',
    exampleImages: ['crm audit 1.png', 'crm audit 2.png'],
  },
  'menu-price-intelligence': {
    description: 'Analyze standalone drink pricing across your entire market. Compares your brands against the market average with breakdowns by platform, geography, cuisine type, and franchise chains.',
    howToUse: 'Select a country and click Analyze Prices. The agent automatically identifies your brands from your workspace configuration and generates a complete pricing analysis.',
    useCases: [
      'Benchmark your drink prices against the overall market average',
      'Identify platforms or regions where your pricing is above or below market',
      'Discover pricing inconsistencies across your brand portfolio',
      'Compare franchise vs independent outlet pricing for your products',
      'Find outlier listings with unusually high or low prices',
    ],
    outputSummary: 'Slide deck with 10 pricing slides + interactive explorer with filters and CSV export',
    keyFeatures: [
      'Market-wide pricing analysis scoped to your brands automatically',
      'Price over time trends: your average vs market average',
      'Brand & subbrand pricing matrix with consistency highlighting',
      'Platform, geography, cuisine, and franchise price comparisons',
      'Interactive explorer with 15+ filters, grouping, and CSV export',
      'Price distribution histogram with statistical markers (P25, median, P75)',
    ],
    exampleImages: [],
  },
  'country-insight-deck': {
    storagePath: 'country-insight-deck.mp4',
    description: `Build a comprehensive QBR-style deck for a specific market with market universe sizing, availability trends, combo analysis, competitor positioning, and AI-generated key takeaways. Designed for country managers preparing quarterly business reviews.`,
    howToUse: `Select a country and choose which sections to include (market universe, availability, combos, competitors, etc.). The agent compiles the latest data into a structured slide deck in about 45 seconds. Customize which sections appear to keep the deck focused on your agenda.`,
    useCases: [
      'Build a quarterly business review deck for your market in minutes instead of hours',
      'Present market universe sizing and platform distribution to leadership',
      'Highlight competitive dynamics and market positioning trends',
      'Share standardized country-level insights across the regional team',
    ],
    outputSummary: 'QBR slide deck with agenda & sections',
    keyFeatures: [
      'Market universe sizing across all platforms',
      'Availability and combo trend analysis',
      'Competitor analysis and market positioning',
      'Customizable section selection',
      'AI-generated key takeaways per section',
      'Exportable as PPTX',
    ],
    exampleImages: ['country insight deck 1.png', 'country insight deck 2.png'],
  },
  'multi-franchise-audit': {
    storagePath: 'multi-franchise-audit.mp4',
    description: `Audit multiple franchises side-by-side in a single exportable table. Compare brand listing rates, combo penetration, image compliance, and DashONE scores across all selected franchises. Ideal for Key Account Managers managing a portfolio of franchise accounts.`,
    howToUse: `Select a country and pick the franchises you want to compare. The agent generates a sortable comparison table with key metrics per franchise. Sort by any column to rank franchises, filter by platform or city, and export the full table to Excel with one click.`,
    useCases: [
      'Compare performance across your top 10 franchise accounts in a single view',
      'Identify which franchises need the most attention on combo or availability metrics',
      'Export the comparison table for inclusion in team reports or leadership updates',
    ],
    outputSummary: 'Sortable, exportable Excel table',
    keyFeatures: [
      'Multi-franchise side-by-side comparison',
      'Sortable columns: score, availability, combo, images',
      'One-click Excel export',
      'Filterable by platform, city, region',
    ],
    exampleImages: ['multi franchise audit 1.png', 'multi franchise audit 2.png'],
  },
  'market-overview': {
    description: `View DashONE metrics across regions, markets, and platforms in an interactive dashboard. Track availability, combo penetration, and scores with trend lines over time. Designed for leadership and strategy teams needing a bird's-eye view of market health across the portfolio.`,
    howToUse: `Select the region or specific markets you want to review. The agent compiles the latest metrics into a dashboard deck with trend analysis. Navigate through slides to see region-by-region and platform-level breakdowns. Export as PPTX or PDF for sharing.`,
    useCases: [
      'Get a quick pulse check on market health across all your active countries',
      'Compare platform performance within a market to identify distribution gaps',
      'Track metric trends over time to spot improvement or deterioration early',
      'Include market overview slides in leadership presentations',
    ],
    outputSummary: 'Interactive dashboard deck',
    keyFeatures: [
      'Region-by-region metric comparison',
      'Platform-level breakdown per market',
      'Trend analysis across refreshes',
      'Exportable as PPTX or PDF',
    ],
  },
  'roi-calculator': {
    description: `Calculate the GMV impact of improving brand availability and menu execution across a franchise network. Model scenarios like "what if availability goes from 60% to 80%?" and generate a presentation-ready deck with the projected financial impact.`,
    howToUse: `Select a country, choose a market segment, and pick the brands to model. The calculator uses real DashONE data to project GMV uplift from improving availability, combo penetration, or image compliance. Adjust the target percentages to see different scenarios. Export the results as a slide deck for FSA meetings.`,
    useCases: [
      'Quantify the revenue opportunity of closing availability gaps for an FSA meeting',
      'Model different improvement scenarios to prioritize the highest-impact actions',
      'Build a business case for investing in menu execution improvements',
      'Present data-backed ROI projections to franchise partners or leadership',
    ],
    outputSummary: 'Interactive calculator + slide deck',
    keyFeatures: [
      'Scenario modeling for availability improvement',
      'GMV impact projection per platform',
      'Presentation-ready output for FSA meetings',
      'Country-specific currency and market data',
    ],
    storagePath: 'roi-calculator.mp4',
    exampleImages: ['/roi-calculator 2.webp', '/roi-calculator 3.webp', '/roi-calculator 4.webp'],
  },
  'performance-news-report': {
    description: `AI-powered search of the latest news about food delivery platforms, aggregators, and market trends. Returns curated insights relevant to your market and brand, powered by Perplexity AI for real-time retrieval.`,
    howToUse: `Select a country or region. The agent searches for the latest news and market developments related to food delivery platforms, aggregator partnerships, and industry trends. Results include curated summaries with direct source links for every insight.`,
    useCases: [
      'Stay up to date on food delivery market trends before a stakeholder meeting',
      'Monitor competitor activity and platform changes in your markets',
      'Include industry context slides in your quarterly business reviews',
    ],
    outputSummary: 'Curated news report with sources',
    keyFeatures: [
      'Real-time news search via Perplexity AI',
      'Filtered to food delivery and ecommerce topics',
      'Source links for every insight',
      'Country/region-specific filtering',
    ],
    storagePath: 'performance-news.mp4',
    exampleImages: ['/fsanews1.webp', '/fsanews2.webp'],
  },
  'custom-data-export': {
    description: `Pull the exact data you need from the DashONE platform in a ready-to-use format. Choose which columns to include — from outlet details and platform info to brand availability, combo presence, and image scores — then apply filters to narrow down to the markets, platforms, or franchises you care about. The export streams directly to your browser, handling datasets up to 2 million rows without timeouts.`,
    howToUse: `Select a country and pick the data level you need (outlet-level or portfolio-level). Choose the columns relevant to your analysis, then apply filters by platform, franchise, city, or date range. Hit export and the file downloads as a CSV that opens cleanly in Excel (UTF-8 with BOM). For pivot-style outputs, enable the pivot option before exporting. Large exports stream progressively so you see progress in real time.`,
    useCases: [
      'Pull outlet-level data for a custom analysis that existing workflow reports don\'t cover',
      'Build your own pivot tables or dashboards in Excel, Tableau, or Power BI',
      'Share filtered datasets with external partners, consultants, or field teams',
      'Create a recurring export to feed internal reporting pipelines',
    ],
    outputSummary: 'Streaming CSV/Excel export',
    keyFeatures: [
      'Up to 2M rows',
      'Column picker: choose exactly the fields you need',
      'Filters by platform, franchise, city, cuisine, and date range',
      'Pivot table mode for pre-aggregated outputs',
      'Works at both outlet and portfolio data levels',
    ],
    exampleImages: ['/export 1.webp', '/export 2.webp'],
    storagePath: 'data-export.mp4',
  },
  'cuisine-audit-deck': {
    description: `Understand how your brand performs across different restaurant categories on food delivery platforms. This workflow breaks down key metrics — availability, combo penetration, and image score — by cuisine type (e.g. Fast Food, Italian, Asian, Burgers) and shows how your brand compares against competitors in each segment. The output is a ready-to-present slide deck you can export as PPTX.`,
    howToUse: `Select a market and choose the cuisine types you want to analyse. The agent automatically pulls your brand's performance data for each cuisine segment, benchmarks it against competitors operating in the same categories, and builds a structured deck. Slide 1 shows how the overall market is split by cuisine type. Slides 2–3 show your brand's availability, combo rate, and image compliance per cuisine. The final section compares your performance head-to-head against competitors across those same metrics.`,
    useCases: [
      'Find out which cuisine categories your brand is well-represented in — and where you are missing outlets',
      'See whether your combo penetration or image compliance is stronger in Fast Food vs Casual Dining vs Asian restaurants',
      'Spot cuisine segments where a competitor is outperforming you and understand the gap',
      'Build a cuisine-level slide deck for a category or sales strategy presentation',
    ],
    outputSummary: 'Slide deck with cuisine-level analysis',
    keyFeatures: [
      'Market overview: how total DSFs are distributed across cuisine types',
      'Your brand\'s availability, combo rate, and image score per cuisine segment',
      'Detailed per-cuisine metrics table for deeper inspection',
      'Side-by-side competitor comparison across all cuisine types',
      'Exportable as PPTX',
    ],
    exampleImages: ['/cuisine 1.webp', '/cuisine 2.webp', '/cuisine 3.webp'],
    storagePath: 'cuisine-audit.mp4',
  },
  'sku-name-audit': {
    description: `Review how accurately your products are named on delivery platforms, store by store. This workflow scores each outlet on how closely its menu item names match the ideal format (subbrand, volume, packaging type) and provides specific suggestions for every listing that falls short. Use the results to guide store-level corrections and improve your overall naming score.`,
    howToUse: `Select a country and choose one or more core brands to audit. The agent scans all outlets carrying those brands, scores each listing for naming completeness, and generates a slide deck with an overview plus a detailed per-outlet table. Expand any outlet row to see its individual SKU listings, the expected name format, and a concrete suggestion for improvement. Export the full breakdown as Excel to share with your field team.`,
    useCases: [
      'Find outlets where product names are truncated, misspelled, or missing key details like volume or packaging',
      'Get a concrete suggestion for each listing showing exactly how the name should read',
      'Prioritise store corrections by sorting outlets from lowest to highest naming accuracy',
      'Export a per-store correction list as Excel for your field or brand compliance team',
    ],
    outputSummary: 'Slide deck + Excel report',
    keyFeatures: [
      'Per-outlet naming accuracy score with colour-coded badges',
      'Expandable outlet rows showing each SKU listing side by side with its expected name',
      'AI-generated improvement suggestion for every non-compliant listing',
      'Filters by platform, franchise, and city to focus on specific segments',
      'Summary deck slides with accuracy distribution charts per brand',
      'Full Excel export with store-level detail for offline review',
    ],
    exampleImages: ['/sku 1.webp', '/sku 2.webp'],
    storagePath: 'sku-name-audit.mp4',
  },
  'drag-and-drop': {
    description: `Answer ad-hoc data questions without leaving DashAI or writing queries. Pick a metric — such as store count, listing count, DashONE Score, or availability — then choose how to break it down (by platform, franchise, city, brand, cuisine, and more). Switch between a table, line chart, or bar chart and the canvas refreshes instantly. Add filters to zero in on a specific segment, and the numbers update in real time.`,
    howToUse: `Select a country to set your market scope. Drag a metric into the canvas area (or pick from the dropdown), then add one or more dimensions to group by. Choose your preferred visualisation — table for exact numbers, bar chart for comparisons, line chart for trends over refreshes. Use the filter panel to narrow results by platform, franchise, city, or any other dimension. Every change updates the canvas live — no waiting, no re-running.`,
    useCases: [
      'Quickly check how many stores list your brand on each platform without running a full report',
      'Build a bar chart comparing availability across franchises to drop into a Slack message or meeting slide',
      'Spot trends by switching to a line chart grouped by refresh period',
      'Drill into a specific city or cuisine segment to answer a stakeholder question on the spot',
    ],
    outputSummary: 'Interactive exploration canvas',
    keyFeatures: [
      'Six metrics: store count, listing count, DashONE Score, availability %, core availability %, image presence %',
      'Thirteen dimensions across outlet and portfolio levels',
      'Instant canvas refresh — no waiting for results',
      'Table, bar chart, and line chart visualisations',
      'Combinable filters: platform, franchise, city, brand, cuisine, and more',
      'Save explorations to your canvas library for later reference',
    ],
    exampleImages: ['/dragdrop 1.webp', '/dragdrop 2.webp'],
    storagePath: 'drag-drop.mp4',
  },
  'kpi-tracker': {
    description: `Monitor your key performance indicators — market share, distribution breadth, and perfect execution scores — across three time horizons: current refresh, prior period, and the same period last year. The output is a styled, multi-sheet Excel workbook ready to paste into your QBR deck or share with leadership. Each sheet breaks down KPIs by brand and subbrand so you can see exactly where progress is being made and where gaps remain.`,
    howToUse: `Select a country and optionally add or remove brands from the default list (only your own brands are shown, competitors are excluded). Hit generate and the agent pulls data for the current, prior period, and last year refreshes, then builds a formatted Excel file with two sheets: one for high-level brand KPIs and one for subbrand-level detail. Download the file and open it in Excel — formatting, column widths, and conditional highlights are already applied.`,
    useCases: [
      'Prepare a KPI summary for your quarterly business review without manual data pulling',
      'Track period-over-period changes in market share and distribution at brand and subbrand level',
      'Identify subbrands that lost distribution or perfect execution score versus last year',
      'Share a ready-to-use Excel report with regional managers or leadership',
    ],
    outputSummary: 'Styled multi-sheet Excel workbook',
    keyFeatures: [
      'Three time horizons: current, prior period, and last year',
      'Brand and subbrand-level KPI breakdown',
      'Pre-formatted Excel with conditional styling',
      'Only your own brands shown — competitors excluded from selection',
      'Market share, distribution, availability, combo, and image KPIs',
      'Instant download — no manual formatting needed',
    ],
    exampleImages: ['/kpi 1.webp'],
    storagePath: 'kpi-tracker.mp4',
  },
  'monthly-kpi-report': {
    description: `Standardized monthly one-pager for each market: DashONE score with month-over-month delta, total DSFs, platform mix, key metric performance, and an AI-classified wins / lost / opportunities / threats summary. Pick one market for a focused brief, several markets to send a per-market deck in one run, or aggregate multiple into a single regional executive cover.`,
    howToUse: `Choose Per market or Aggregated, then pick the markets you want. Per market generates one one-pager per country (great for client-by-client distribution). Aggregated rolls every selected market into a single executive view. Output is a PDF. Once the canvas opens, hit Schedule to have the same workflow re-run automatically each month and emailed to your recipients.`,
    useCases: [
      'Send the same monthly one-pager to client stakeholders, one per market',
      'Track DashONE score and DSF trends month-over-month at a glance',
      'Surface wins, lost, opportunities, and threats without manual analysis',
      'Roll multiple countries into a single regional executive review',
      'Set up a recurring monthly email that re-runs on the latest refresh',
    ],
    outputSummary: 'PDF one-pager',
    keyFeatures: [
      'One-pager template: header KPIs, platform mix, 4-quadrant insights',
      'Per market mode generates one report per country in a single run',
      'Aggregated mode merges selected markets into one executive cover',
      'AI-generated executive summary and trend classification',
      'Schedulable monthly via the canvas email scheduler (PDF)',
    ],
    exampleImages: [],
    storagePath: '',
  },
  'monthly-scorecard': {
    description: `Generate the PEP FR monthly multi-KPI scorecard — a 14-worksheet styled Excel rolling up FSA presence, market share, portfolio compliance, naming variants, image audit, combo activation, pricing, and chain compliance into a single client-facing report.`,
    howToUse: `Pick the current refresh, the previous refresh for month-over-month comparison, and an end-of-last-year refresh. Optionally toggle individual sections off if you only want a subset. Click Generate Excel; the workflow runs 13 ClickHouse section queries in parallel, builds a 14-worksheet workbook (Guidance + 13 data sections), and renders a 14-tab Canvas preview before download.`,
    useCases: [
      'Send PEP FR the monthly multi-KPI scorecard each refresh cycle',
      'Audit naming and image execution at chain level',
      'Track combo activation and core portfolio penetration over time',
      'Identify chains with PEP-brand availability gaps',
    ],
    outputSummary: 'Styled 14-worksheet Excel + Canvas preview',
    keyFeatures: [
      'PEP FR-only — uses hardcoded brand, chain, SKU, and rule lists',
      '13 KPI sections in a single Excel with cross-tab navigation',
      'Naming and image audit per chain (Pepsi Max, Lipton Green, wrong logo, wrong product image)',
      'Chain × packaging-bucket pricing for ~50 French chains',
      'Visually equivalent to the legacy Google Sheet template (except brand logos)',
    ],
    exampleImages: [],
    storagePath: '',
  },
  'platform-distribution-gaps': {
    description: `Identify outlets or brands that are listed on one delivery platform but missing from another. By matching outlets across platforms using unique identifiers, this workflow reveals distribution gaps — helping you prioritize where to expand listings for maximum reach.`,
    howToUse: `Select a country and choose the platforms to compare (e.g., Uber Eats vs Deliveroo). Optionally filter by franchise or region. The agent cross-references outlet listings and generates a deck showing which outlets have your brand on one platform but not the other, plus a summary of gap sizes by franchise and region.`,
    useCases: [
      'Find outlets where your brand is on Uber Eats but missing from Deliveroo (or vice versa)',
      'Prioritize platform expansion by identifying the highest-volume gaps',
      'Build a targeted activation list for field teams by region and franchise',
      'Track cross-platform distribution parity over time',
    ],
    outputSummary: 'Slide deck with gap analysis',
    keyFeatures: [
      'Proprietary cross-platform outlet matching',
      'Gap analysis at outlet, brand, and SKU level',
      'Franchise and region-level aggregation',
      'Prioritized gap list for field team activation',
      'Exportable as PPTX',
    ],
  },
  'win-back-lost-customers': {
    description: `Surface outlets that previously listed your brand but no longer do. Because these outlets have already validated menu fit and supplier setup, re-acquisition is faster and cheaper than targeting net-new outlets. Filter by city, platform, or competitor displacement to build a prioritized call list for your sales team.`,
    howToUse: `Select a country and a lookback period (most recent refresh pair, 3, 6, or 12 months). Optionally narrow to a specific brand instead of all client brands. The agent identifies every outlet that listed your brand in the lookback window but is gone in the latest refresh, then checks whether a competitor has moved in. Results appear in a KPI strip and a paginated outlet table you can export to Excel.`,
    useCases: [
      'Build a re-acquisition call list for sales reps: outlets that already know your brand',
      'Identify which platforms are losing distribution fastest and focus field effort there',
      'Spot competitor displacement: which brand moved in after yours left',
      'Compare single-brand vs all-brand churn to detect SKU-level issues vs account-level issues',
    ],
    outputSummary: 'KPI strip and exportable outlet table (CSV/Excel)',
    keyFeatures: [
      'DSF-level granularity: one row per outlet per platform',
      'Lookback options: most recent refresh, 3, 6, or 12 months',
      'Competitor displacement flag per outlet (brand that replaced yours)',
      'Filter by city, platform, or outlet name with server-side pagination',
      'Excel export via streaming CSV with UTF-8 BOM for non-ASCII outlet names',
    ],
  },
  'pack-type-gaps': {
    description: `Classify every SKU listing of your core brands as Can, Bottle, or Undefined, then roll that up to a per-brand and per-outlet pack verdict (Can / Bottle / Mixed / Undefined). Surface exactly which outlets carry only one pack format so field teams can push the missing one, e.g. cans vs bottles across your core brands.`,
    howToUse: `Select a country, choose whether to view by storefront (DSF) or outlet, and optionally narrow to specific core brands or a restaurant/non-restaurant segment. The agent classifies every listing, rolls it up per outlet, and shows a KPI strip plus a filterable table you can export to CSV.`,
    useCases: [
      'Find outlets that only carry your brand in bottle format and are missing the can (or vice versa)',
      'Prioritize a field-team activation list by city or platform for the missing pack type',
      'Compare pack-type mix across your top 3 core brands at a glance',
      'Export the full outlet-level detail, including Google place ID and execution metrics, for offline analysis',
    ],
    outputSummary: 'KPI strip and exportable outlet table (CSV)',
    keyFeatures: [
      'Storefront (DSF) or outlet grain toggle',
      'Per-brand and overall pack verdict: Can / Bottle / Mixed / Undefined',
      'KPI strip with DSF-grain headline counts and outlet-grain sub-line',
      'Filter by city, platform, or predominant pack with server-side pagination',
      'CSV export via streaming download with UTF-8 BOM for non-ASCII outlet names',
    ],
  },
  'forge-explorer': {
    storagePath: 'forge-explorer.mp4',
    description: `Interactive analytics dashboard combining DSF (Digital Store Front) and beverage exploration. Analyze outlet segmentation, cuisine types, geographic distribution, platform coverage, and combo availability — then switch to the Beverage Explorer for manufacturer market shares, brand analysis, pricing distribution, and category breakdowns. All charts are cross-filterable and drill-down enabled.`,
    howToUse: `Select a country and optionally filter by segment (Restaurant / Non-Restaurant). The dashboard loads with a KPI strip showing key metrics, then explore two main tabs: DSF Explorer (outlet-focused charts) and Beverage Explorer (product-focused charts). Click any chart element to cross-filter all other widgets. Use the filter panel on the left to narrow results. Switch to the listing tabs for full paginated data tables.`,
    useCases: [
      'Understand market structure: how many DSFs exist, what segments and cuisines dominate, and which platforms have the most coverage',
      'Identify beverage opportunities: which manufacturers lead, what brand shares look like, and where pricing gaps exist',
      'Drill into geographic areas to find regional patterns in outlet density or beverage distribution',
      'Cross-filter by cuisine type, platform, or segment to find niche opportunities for your brands',
      'Export DSF or beverage listings for offline analysis and field team activation',
    ],
    outputSummary: 'Interactive dashboard with cross-filtering',
    keyFeatures: [
      'KPI strip with 7 key metrics (DSFs, beverages, own brand penetration, combos)',
      'DSF Explorer: segmentation, cuisine types, area drill-down, price levels, platforms, combo analysis',
      'Beverage Explorer: manufacturer/brand market shares, pricing histogram with stats, category breakdown',
      'Full cross-filtering — click any chart element to filter all widgets',
      'Drill-down on area tables (Level 1 → Level 2 → Postal Code) and brand charts (Brand → Sub Brand)',
      'Left sidebar with 30+ filters: geography, segments, outlet details, score, combos, portfolio',
      'Server-side paginated DSF and beverage listing tables with sort and export',
      'Canvas persistence — filters and state saved when reopening from Saved Canvas',
    ],
    exampleImages: ['forge explorer 1.png', 'forge explorer 2.png'],
  },
  'ons-explorer': {
    description: 'Interactive One Number Score dashboard for CC ASP markets. View the composite ONS score and its three components — Digital Storefront Availability, Core SKU Availability, and Activation — across 9 countries.',
    howToUse: 'Click Explore to load the overview matrix. Use tabs to switch between countries. On country pages, filter by Platform, Area Level 1, or Area Level 2.',
    useCases: [
      'Compare ONS performance across all ASP countries at a glance',
      'Identify countries with low Core SKU Availability for targeted action',
      'Track Activation trends by switching refresh months',
      'Drill into specific platforms or regions within a country',
    ],
    outputSummary: 'Interactive ONS dashboard with country drill-down',
    keyFeatures: [
      'Overview heatmap matrix across 9 ASP countries',
      'Three ONS components: DSF Availability, Core SKU, Activation',
      'Country-level drill-down with platform and area filters',
      'Configurable refresh period selection',
    ],
  },
  'map-explorer': {
    storagePath: 'map-explorer.mp4',
    description: `Explore your outlet landscape on an interactive map. See every Digital Store Front plotted geographically with score-based color coding, clustered pins that expand on zoom, and heatmap overlays for density, score, availability, and more. Draw polygon areas to filter outlets and compare KPI metrics across regions. Identify geographic patterns, coverage gaps, and high-opportunity areas at a glance.`,
    howToUse: `Select a country and click "Start Exploration". The map loads all outlets as clustered, score-colored pins. Use the filter panel on the left to narrow by geography, platform, franchise, cuisine, score range, and more. Toggle the heatmap for density or metric overlays. Draw a polygon to filter outlets within a specific area. Click any outlet pin to see its full detail including DashONE Score, availability, and SKU breakdown.`,
    useCases: [
      'Identify geographic coverage gaps where your brand has low or no presence compared to competitors',
      'Spot high-density outlet clusters to prioritize for field activation or promotions',
      'Compare DashONE Score distribution across regions using the heatmap overlay',
      'Draw custom areas on the map to analyze KPIs for specific neighborhoods or territories',
      'Click individual outlets to review their performance before a key account visit',
    ],
    outputSummary: 'Interactive map dashboard with filterable outlet pins, heatmaps, and geographic analytics',
    keyFeatures: [
      'Interactive Mapbox map with smooth pan/zoom and score-colored outlet pins',
      'Clustered pins that expand on click to reveal individual outlets',
      'Heatmap overlay with selectable metrics: density, score, availability, combo rate',
      'Collapsible filter panel with 30+ filter dimensions',
      'Draw-to-filter polygon tool for geographic area selection',
      'Outlet detail modal on pin click with full DashONE performance data',
      'KPI strip with live metrics that update with filter changes',
    ],
    exampleImages: ['map explorer 1.png', 'map explorer 2.png'],
  },
  'image-compliance': {
    description: `Audit image compliance across an entire market. Select a country and filters, and DashAI generates a full image presence report covering all stores, brands, and SKUs in scope. Identify which outlets and products are missing images on delivery platforms, broken down by brand, franchise, segment, and platform.`,
    howToUse: `Select a country and optionally narrow the scope with platform, segment, store type, franchise, and brand filters. Click Generate Report. DashAI queries all in-scope listings, computes image presence metrics at every level (market, brand, SKU, franchise, store), and produces a slide deck plus an interactive drill-down table. Use the deck for stakeholder presentations and the table to build targeted action lists.`,
    useCases: [
      'Identify brands and SKUs with the lowest image coverage across delivery platforms',
      'Compare image compliance between franchise chains and independent stores',
      'Build a prioritised target list of stores that need image uploads',
      'Track image presence improvement over time across monthly refreshes',
    ],
    outputSummary: 'Slide deck + interactive store table',
    keyFeatures: [
      'Market-level image presence KPIs',
      'Per-brand and per-SKU image breakdown',
      'Franchise and segment comparison slides',
      'Top 10 worst stores and SKUs for quick action',
      'Platform comparison when multiple platforms selected',
      '12-month image presence trend',
      'Interactive store table with listing-level expansion',
      'Placeholder columns for future image audit scoring',
    ],
  },
  'koasp-grab-5star-audit': {
    description: 'Multi-country audit of the Coca-Cola Grab 5-Star outlet universe across ID, MY, TH, VN, PH, SG. Profiles brand-level KO presence, image and name coverage, DashONE score distribution, and the three behavioural segments (Have KO, Other Beverages, No Beverages). Tracks segment trend and bucket-level KO change across refreshes, ranks top cities per market, and lands an Excel + PDF + PPTX deliverable for the monthly client cadence.',
    howToUse: 'Pick one or more SEA markets and a refresh (defaults to the freshest cross-market). The agent fans out per-country queries against ko_asp_grab data in dashpet_prod and assembles an 11-slide deck. Use the Canvas tab for the slide narrative, the Dashboard tab for filterable KPI tiles and charts (Excel export), and the List tab to drill into individual outlets.',
    useCases: [
      'Prep the monthly Mukesh check-in with refreshed KO presence across 6 SEA markets',
      'Identify cuisine buckets and cities with the biggest KO recruitment opportunity',
      'Track segment movement (Have KO / Competitor / No Beverages) across refreshes',
      'Quantify image and canonical-name coverage on the Has-KO base for execution quality',
      'Spot per-brand KO gaps by country and brand (e.g. Sprite under-listing in Indonesia)',
    ],
    outputSummary: '11-slide deck (Canvas) + filterable Dashboard with Excel export + paginated Outlet List drill-down',
    keyFeatures: [
      'Three behavioural segments per market with the three-plays framing from Mukesh',
      'Brand-level Has-KO breakdown across the 7 KO brands (gaps surfaced explicitly)',
      'Image and canonical-name coverage rates on the Has-KO outlet base',
      'DashONE score distribution by decile, with p25 / p50 / p75 markers',
      'Top-5 cities by Has-KO outlets per country, mapped via geographic view',
      'Cuisine-bucket KO comparison: this refresh vs last (dumbbell, Western & Burger and Fried Chicken & QSR prioritised)',
      'Server-paginated outlet list with country, cuisine bucket, has-KO and has-competitor columns',
      'PDF + PPTX from the Canvas, Excel from the Dashboard, brand-faithful Dashmote palette',
    ],
  },
  '1p-qsr-app-audit': {
    description: 'Audit QSR franchise app compliance for PepsiCo beverage availability, standalone listings, combo inclusion, image quality, and pricing. Scores every store on four dimensions and identifies priority locations for field activation.',
    howToUse: 'Select a country (US) and franchise (e.g., KFC). The agent scrapes all matching stores, evaluates each PepsiCo product across availability, standalone, combo, and image dimensions, then generates a multi-slide audit deck with geographic maps and priority store lists.',
    useCases: [
      'Prepare for a QSR franchise meeting with a complete beverage compliance scorecard',
      'Identify stores missing key PepsiCo products or listing them only in combos',
      'Compare PepsiCo share-of-menu against competitors like Dr Pepper and Sweet Tea',
      'Find geographic clusters of underperforming stores for targeted field activation',
      'Track price consistency across stores and flag outlier pricing',
    ],
    outputSummary: 'Audit deck with executive summary, product matrix, price analysis, competitor landscape, compliance gaps, priority stores, and geographic map',
    keyFeatures: [
      'Four-dimension scoring: availability, standalone, combo, and image compliance',
      'Product-level matrix with availability rates, combo rates, and sample images',
      'Price analysis with min/max/avg per product and price tier distribution',
      'Competitor landscape: Dr Pepper, Sweet Tea, Lemonade penetration rates',
      'PepsiCo share-of-menu calculation across all stores',
      'Priority store list ranked by composite score for field activation',
      'Geographic distribution map with state/city breakdowns',
      'AI-generated insights and actionable recommendations',
    ],
  },
  '1p-epl-app-audit': {
    description: 'Audit El Pollo Loco app compliance for Coca-Cola beverage availability, combo inclusion, image quality, and pricing across all store locations. Evaluates both the full portfolio (10 SKUs) and core SKUs (Coca-Cola, Coca-Cola Zero Sugar, Diet Coke, Sprite).',
    howToUse: 'Select a country (US) and franchise (El Pollo Loco). The agent evaluates every store location for Coca-Cola product compliance, generates scores on availability and combo dimensions, and produces a multi-slide audit deck with specialty drink analysis and geographic maps.',
    useCases: [
      'Review Coca-Cola product compliance across all El Pollo Loco locations before a brand meeting',
      'Identify stores missing core SKUs or failing to include drinks in combo meals',
      'Analyze specialty drink adoption (Horchata, Berrychata, Cold Foam variants)',
      'Compare Dr Pepper presence as a competitive threat across locations',
      'Track pricing consistency for regular, large, and water size tiers',
    ],
    outputSummary: 'Audit deck with executive summary, product matrix, core vs full portfolio scoring, price analysis, specialty drinks, competitor presence, priority stores, and geographic map',
    keyFeatures: [
      'Dual scoring: full portfolio (10 SKUs) and core SKU (4 SKUs) dimensions',
      'Product-level availability, combo, and image rates with store counts',
      'Price analysis by size tier (regular, large, bottle water) with statistical breakdown',
      'Specialty drink adoption rates (Horchata, Cold Foam variants, etc.)',
      'Dr Pepper competitive presence tracking',
      'Coca-Cola share-of-menu calculation',
      'Priority store list ranked by composite score',
      'Geographic distribution with state and city breakdowns',
      'Drink image gallery from actual store menus',
    ],
  },
  'category-market-share': {
    storagePath: 'category-market-share.mp4',
    description: 'Analyze how brands share the market within a product category (e.g., soft drinks, snacks). Track market share trends over three data refreshes and drill down into SKU-level DSF listings per brand. Useful for portfolio planning and competitive benchmarking within a category.',
    howToUse: 'Select a country, category, segment (Restaurant or Non-Restaurant), franchise, and up to 5 brands to compare. The agent computes Market Share % = Brand DSF Count / Total Category DSF Count × 100 for each brand across the last 3 refreshes and generates trend charts plus a reference benchmark line. Switch to the DSF tab for store-level SKU listings with filters and Excel export.',
    howToUseCurated: 'Pick a franchise, and optionally narrow the platform or the SKU list. Your workspace’s SKUs arrive pre-selected, so pressing Start Analysis is enough. The agent computes Share % = SKU DSF Count / Total Category DSF Count × 100 per SKU across the last 3 refreshes and generates trend charts plus a reference benchmark line. Switch to the DSF tab for store-level listings with filters and Excel export.',
    useCases: [
      'Compare market share trends for your brands vs competitors within a category',
      'Identify which brands are gaining or losing share across recent data refreshes',
      'Drill into SKU-level listings to understand which products drive category share',
      'Benchmark your category share against the fair-share reference line (100% / N brands)',
    ],
    outputSummary: 'Trend charts (Market Share % + DSF Counts) + SKU-level DSF table',
    keyFeatures: [
      'Market Share % trend line across 3 refreshes per brand',
      'Absolute DSF count trend alongside percentage view',
      'Fair-share benchmark reference line (100% / total brands in category)',
      'SKU-level DSF listings with server-side pagination',
      'Filterable by brand, subbrand, refresh',
      'Excel export for DSF listings',
      'PPTX/PDF export for trend charts',
    ],
    exampleImages: ['category market share 1.png', 'category market share 2.png'],
  },
  // scaffold:wf-detail — tools/scaffolds/scaffold.sh adds a new workflow's detail content below this line.
};
